import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Grid,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Fade
} from '@mui/material';
import { IconSearch, IconPlus, IconPhoto, IconRulerMeasure } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import { encodePathSegments } from '../../../../../utils/media-url';
import { formatImovelPrice } from '../../../../../utils/currency';
import { STATUS_OPTIONS } from './CreateImovel';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:8000/api/';

const STATUS_COLOR = {
    disponivel: 'success',
    pendente: 'warning',
    reservado: 'warning',
    indisponivel: 'default',
    vendido: 'default',
    alugado: 'default',
    encerrado: 'default'
};

const ViewAllImoveis = () => {
    const [imoveis, setImoveis] = useState([]);
    const [appId, setAppId] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFiltro, setStatusFiltro] = useState('');
    const [cidadeFiltro, setCidadeFiltro] = useState('');
    const history = useHistory();

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarImoveis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const listarImoveis = async () => {
        const res = await api.get(`v1/apps/${appId}/imoveis/`);
        setImoveis(res.data);
    };

    const getImageUrl = (path) => `${BACKEND_SERVER}v1/apps/${appId}/media/${encodePathSegments(path)}`;

    const getCoverImage = (imovel) => {
        const imagens = imovel.imagens || [];
        const capa = imagens.find((img) => img.principal) || imagens[0];
        return capa ? getImageUrl(capa.url) : null;
    };

    const cidadesDisponiveis = [...new Set(imoveis.map((i) => i.cidade).filter(Boolean))];

    const filtered = imoveis.filter((i) => {
        if (search && !i.titulo?.toLowerCase().includes(search.toLowerCase())) return false;
        if (statusFiltro && i.status !== statusFiltro) return false;
        if (cidadeFiltro && i.cidade !== cidadeFiltro) return false;
        return true;
    });

    return (
        <Box p={4} sx={{ minHeight: '100vh' }}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={2}
                mb={3}
            >
                <Box>
                    <Typography variant="h4" fontWeight="700">
                        Imóveis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {imoveis.length > 0 ? `${imoveis.length} imóve${imoveis.length === 1 ? 'l' : 'is'} cadastrado${imoveis.length === 1 ? '' : 's'}` : 'Gerencie o catálogo de imóveis'}
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<IconPlus size={18} />}
                    onClick={() => history.push('/dashboard/config/new/imovel')}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}
                >
                    Novo Imóvel
                </Button>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={4}>
                <TextField
                    size="small"
                    placeholder="Buscar imóveis..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 }, background: '#fff', borderRadius: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconSearch size={18} />
                            </InputAdornment>
                        )
                    }}
                />

                <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFiltro}
                    onChange={(e) => setStatusFiltro(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 180 }, background: '#fff', borderRadius: 2 }}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                            {o.label}
                        </MenuItem>
                    ))}
                </TextField>

                {cidadesDisponiveis.length > 0 && (
                    <TextField
                        select
                        size="small"
                        label="Cidade"
                        value={cidadeFiltro}
                        onChange={(e) => setCidadeFiltro(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 200 }, background: '#fff', borderRadius: 2 }}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {cidadesDisponiveis.map((c) => (
                            <MenuItem key={c} value={c}>
                                {c}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Stack>

            <Grid container spacing={3}>
                {filtered.map((imovel) => {
                    const capa = getCoverImage(imovel);
                    return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={imovel.id}>
                            <Fade in timeout={300}>
                                <Card
                                    onClick={() => history.push(`/dashboard/config/imovel/${imovel.id}`)}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.25s ease',
                                        background: '#fff',
                                        '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 20px 40px rgba(0,0,0,0.12)' },
                                        '&:hover .imovel-cover-img': { transform: 'scale(1.08)' }
                                    }}
                                >
                                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                                        {capa ? (
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={capa}
                                                className="imovel-cover-img"
                                                sx={{ objectFit: 'cover', transition: 'transform 0.35s ease' }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: 200,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 1,
                                                    bgcolor: 'rgba(0,0,0,0.06)',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                <IconPhoto size={32} />
                                                <Typography variant="caption">Sem foto</Typography>
                                            </Box>
                                        )}

                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                height: '60%',
                                                background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                                                pointerEvents: 'none'
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={STATUS_OPTIONS.find((o) => o.value === imovel.status)?.label || imovel.status}
                                            color={STATUS_COLOR[imovel.status] || 'default'}
                                            sx={{ position: 'absolute', top: 10, right: 10, fontWeight: 600 }}
                                        />

                                        <Typography
                                            sx={{ position: 'absolute', bottom: 10, left: 12, color: '#fff', fontWeight: 700, fontSize: 16 }}
                                        >
                                            {formatImovelPrice(imovel.valor, imovel.valor_a_partir_de)}
                                        </Typography>
                                    </Box>

                                    <CardContent sx={{ p: 2.5 }}>
                                        <Typography
                                            fontWeight="600"
                                            sx={{ fontSize: 15, mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                        >
                                            {imovel.titulo}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            {imovel.bairro ? `${imovel.bairro}, ` : ''}
                                            {imovel.cidade ? `${imovel.cidade}${imovel.estado ? '/' + imovel.estado : ''}` : 'Sem endereço'}
                                        </Typography>

                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Chip
                                                label={`${imovel.tipo} • ${imovel.finalidade}`}
                                                size="small"
                                                sx={{ borderRadius: 2, fontWeight: 500, textTransform: 'capitalize' }}
                                            />

                                            {(imovel.area_m2 || imovel.area_util_m2) && (
                                                <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
                                                    <IconRulerMeasure size={14} />
                                                    <Typography variant="caption">{imovel.area_util_m2 || imovel.area_m2} m²</Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Fade>
                        </Grid>
                    );
                })}
            </Grid>

            {filtered.length === 0 && (
                <Box mt={10} textAlign="center" color="text.secondary">
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2
                        }}
                    >
                        <IconPhoto size={30} />
                    </Box>
                    <Typography variant="h6">Nenhum imóvel encontrado</Typography>
                    <Typography variant="body2">Tente buscar outro termo, ajustar os filtros ou criar um novo imóvel</Typography>
                </Box>
            )}
        </Box>
    );
};

export default ViewAllImoveis;
